import { useCallback, useEffect } from 'react';
import {
  CONSUMER_CMS_SHOPPING_CART,
  IGenericErrorCMS,
  IGlobalConfigCMS,
  IPayments,
  useCmsConfig,
} from '@vfit/consumer/data-access';
import { API, getClickToCallWithKeyOrUrl, trackView } from '@vfit/shared/data-access';
import { EMethodCode, ICommonData, ISIAFlowType } from '@vfit/shared/models';
import { PaymentMethod } from '@vfit/consumer/components';
import {
  DEFAULT_CREDIT_CARD,
  organizePaymentMethod,
  organizePaymentMethodError,
  organizeUpfrontSelectionCard,
} from '../upfrontPaymentCard.utils';
import { useCheckout } from '../../../../iBuyFixed.context';
import { useAuthorizeAuthorizationFlow } from '../../../hooks/UseAuthorizeAuthorizationFlow/useAuthorizeAuthorizationFlow';
import PreselectedUpfrontPaymentTagging from './preselectedUpfrontPayment.tagging';
import { useQueryClient } from 'react-query';

const UpfrontPayment = ({ handleOnChangeEnableGoNext, handleGoPrevSlide }: ICommonData) => {
  const queryClient = useQueryClient();
  const goNextButton = document.getElementById('sticky-offer-next');
  const dataFromCms = useCmsConfig(CONSUMER_CMS_SHOPPING_CART, API.CMS_GET_PAYMENTS) as IPayments;
  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const { title, buttonLabel } = organizePaymentMethod(dataFromCms);
  const { titlePaymentError, messagePaymentError } = organizePaymentMethodError(dataFromCms);
  const {
    descriptionCC,
    descriptionDC,
    descriptionPrepaid,
    descriptionPaypal,
    titleCC,
    titleDC,
    titlePrepaid,
    titlePaypal,
    paymentMethod,
  } = organizeUpfrontSelectionCard(dataFromCms, cmsGlobalConfig);
  const {
    currentStepKey,
    checkoutErrors,
    owningData,
    upfrontPayment,
    isModalButtonSticky,
    product,
  } = useCheckout();
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR
  ) as IGenericErrorCMS;
  const selectedItem = paymentMethod?.find(
    (el) => el.value.toLowerCase() === EMethodCode.CREDIT_CARD.toLowerCase()
  );

  const { authorize } = useAuthorizeAuthorizationFlow(
    selectedItem?.value,
    ISIAFlowType.INSTANT,
    'verify'
  );

  const isSuccesAuthorize = authorize?.isSuccess;
  const isErrorAuthorize = authorize?.isError;

  const showError = () => {
    const action = () => {
      if (handleGoPrevSlide) handleGoPrevSlide();
    };
    checkoutErrors?.showAndTrackError?.(
      genericError,
      action,
      authorize?.error,
      titlePaymentError,
      messagePaymentError,
      action,
      'Chiudi',
      () =>
        getClickToCallWithKeyOrUrl(
          queryClient,
          product?.genericErrorCallMeNow?.url || '',
          product,
          action,
          currentStepKey,
          authorize?.error
        ),
      product?.genericErrorCallMeNow?.label || '',
      isModalButtonSticky,
      true
    );
  };

  const getDynamicSubtitle = useCallback(() => {
    const actualMethodType = upfrontPayment?.type?.toLowerCase();
    switch (actualMethodType) {
      case EMethodCode.CREDIT_CARD?.toLowerCase():
        return descriptionCC;
      case EMethodCode.DEBIT_CARD?.toLowerCase():
        return descriptionDC;
      case EMethodCode.PREPAID_CARD?.toLowerCase():
        return descriptionPrepaid;
      case EMethodCode.PAYPAL?.toLowerCase():
        return descriptionPaypal;
      case EMethodCode.BANK_ACCOUNT?.toLowerCase():
      case EMethodCode.WALLET?.toLowerCase():
      default:
        return '';
    }
  }, [upfrontPayment]);

  useEffect(() => {
    if (isErrorAuthorize) {
      showError();
    }
  }, [isErrorAuthorize]);

  useEffect(() => {
    handleOnChangeEnableGoNext?.(isSuccesAuthorize);
  }, [isSuccesAuthorize]);

  useEffect(() => {
    if (!selectedItem) {
      showError();
    }
    const trackGoNext = () => {
      trackView({
        event_name: 'ui_interaction',
        event_label_track: 'Credit Card',
        event_category: 'users',
        event_action: 'click',
        tracking_type: 'link',
      });
    };
    goNextButton?.addEventListener('click', trackGoNext);
    return () => {
      goNextButton?.removeEventListener('click', trackGoNext);
    };
  }, []);

  return (
    <>
      <PreselectedUpfrontPaymentTagging />
      <PaymentMethod
        owningIndividual={owningData.owningIndividual}
        handleGoPrevSlide={handleGoPrevSlide}
        payMean={upfrontPayment || DEFAULT_CREDIT_CARD}
        showLoader={!isSuccesAuthorize}
        config={{
          title,
          subtitle: getDynamicSubtitle(),
          buttonLabel,
          titlePaymentError,
          messagePaymentError,
          yourCreditCardTitle: titleCC,
          yourDebitCardTitle: titleDC,
          yourPrepaidCardTitle: titlePrepaid,
          yourPaypalTitle: titlePaypal,
        }}
      />
    </>
  );
};

export default UpfrontPayment;

